import { ReactNode } from 'react'

import clsx from 'clsx'

import {
  adjacentDistance,
  centerOffset,
  dotSpacing,
  dotsListWidth,
  maxVisible,
} from 'shared/ui/carousel/components/model.ts'

import s from '../styles.module.scss'

interface CustomDotsProps {
  dots: ReactNode[]
  currentSlide: number
  totalSlides: number
  size?:
    | 'medium'
    | 'large'
    | 'small'
    | 'aspect'
    | 'square'
    | 'collectionsHorizontal'
    | 'collectionsGrid'
}

export const CustomDots = ({
  dots,
  currentSlide,
  totalSlides,
  size = 'medium',
}: CustomDotsProps) => {
  const isSmallSize = size === 'small'

  const getDotPosition = (index: number) => {
    if (totalSlides <= maxVisible) {
      const totalWidth = totalSlides * dotSpacing
      const startOffset = (dotsListWidth - totalWidth) / 2
      return startOffset + index * dotSpacing
    }

    const scrollOffset = centerOffset - currentSlide * dotSpacing
    const maxScroll = (totalSlides - maxVisible) * dotSpacing
    const limitedScrollOffset = Math.max(-maxScroll, Math.min(0, scrollOffset))

    return index * dotSpacing + limitedScrollOffset + 1
  }

  const getDotPositionClass = (index: number, currentSlide: number) => {
    const isActive = index === currentSlide
    const distanceFromActive = Math.abs(index - currentSlide)

    if (isActive) return 'center'
    if (distanceFromActive <= adjacentDistance) return 'adjacent'
    return 'outer'
  }

  return (
    <div className={clsx(s.customDotsContainer, isSmallSize && s.small)}>
      <ul className={s.customDotsList}>
        {dots.map((dot, index) => {
          const isActive = index === currentSlide
          const positionClass = getDotPositionClass(index, currentSlide)

          return (
            <li
              key={index}
              className={clsx(s.customDot, {
                [s.active]: isActive,
                [s.center]: positionClass === 'center',
                [s.adjacent]: positionClass === 'adjacent',
                [s.outer]: positionClass === 'outer',
              })}
              style={{
                left: `${getDotPosition(index)}px`,
                transform: isActive
                  ? 'translateY(-50%) scale(1.1)'
                  : 'translateY(-50%) scale(1)',
                transition: 'all 0.5s ease',
              }}
            >
              {dot}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
